import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { environment } from '../../../../../../../environments/environment.local';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  GenericTableComponent,
  ColumnConfig,
  FieldType,
  GlobalAction,
  ModalFieldConfig,
  ModalMode,
  ModalTabConfig,
  TableAction,
  TableActionConfig,
  GlobalApiConfigService,
  HeaderConfigurationService,
  ModelApiService,
  ButtonType,
  ButtonConfig,
  MoreDataConfig,
  RowStyleConfig,
  SortDirection,
  SortMode,
  TableSortConfig,
} from '@solcre-org/core-ui';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DataStoreService } from '../../../../../../shared/services/data-store.service';
import { ClientRateModel } from '../../../../../../shared/models/client-rate.model';
import { PortModel } from '../../../../../../shared/models/port.model';
import { CargoTypeModel } from '../../../../../../shared/models/cargo-type.model';
import { portNameTemplate } from '../../../../../../shared/templates/portNameTemplate';
import { portUriTemplate } from '../../../../../../shared/templates/portUriTemplate';
import { cargoTypeUriTemplate } from '../../../../../../shared/templates/cargoTypeUriTemplate';
import { RateMeasurementUnit } from '../../../../../../shared/enums/rate-measurement-unit.enum';
import { RateTerms } from '../../../../../../shared/enums/rate-terms.enum';
import { CurrencyModel } from '../../../../../../shared/models/currency.model';
import { currencyUriTemplate } from '../../../../../../shared/templates/currencyUriTemplate';

@Component({
  selector: 'app-rates',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './rates.html',
})
export class Rates implements OnInit, AfterViewInit, OnDestroy {
  private translateService: TranslateService = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private globalApiConfigService = inject(GlobalApiConfigService);
  private headerConfigurationService = inject(HeaderConfigurationService);
  private modelApiService = inject(ModelApiService);
  private router = inject(Router);
  private dataStoreService = inject(DataStoreService);

  @ViewChild('historyTemplate', { static: true })
  historyTemplate!: TemplateRef<any>;
  @ViewChild('ratesTable', { static: true })
  ratesTable!: GenericTableComponent<ClientRateModel>;

  fieldErrors = signal<Map<string, string[]>>(new Map());
  showValidationErrors = signal<boolean>(false);
  modalMode = signal<ModalMode>(ModalMode.EDIT);
  editedData = signal<any | null>(null);
  clientId = signal<string | null>(null);
  endpoint = computed<string>(() => {
    return environment.API.CLIENTS_RATES_URI.replace(
      ':id',
      this.clientId() || ''
    );
  });
  customParams = computed<Record<string, any>>(() => {
    return {
      client: this.clientId(),
    };
  });
  sortConfig = signal<TableSortConfig>({
    mode: SortMode.SERVER,
    multiColumn: false,
    serverSortParam: 'order',
    defaultSort: [
      { key: 'isActive', direction: SortDirection.DESC },
      { key: 'version', direction: SortDirection.DESC },
    ],
  });
  rowStyleConfigs = signal<RowStyleConfig<ClientRateModel>[]>([
    {
      condition: (row: ClientRateModel) => this.isActiveRate(row),
      className: 'rate-row--active',
    },
    {
      condition: (row: ClientRateModel) => !this.isActiveRate(row),
      className: 'rate-row--historical',
    },
  ]);

  moreData = signal<MoreDataConfig<any>[]>([
    {
      key: 'ports',
      endpoint: environment.API.PORTS_URI + '?pagination=false&order[fullName]=asc',
      model: PortModel,
    },
    {
      key: 'cargoTypes',
      endpoint: environment.API.CARGO_TYPES_URI + '?pagination=false',
      model: CargoTypeModel,
    },
    {
      key: 'currencies',
      endpoint: environment.API.CURRENCY_URI,
      model: CurrencyModel,
    }
  ]);

  columns: ColumnConfig<ClientRateModel>[] = [];

  modalFieldsTabs = computed<ModalTabConfig<ClientRateModel>[]>(
    () =>
      [
        {
          tabId: 'rates_data',
          tabLabel: 'rates.title',
          fields: [
            {
              label: 'contact.client',
              key: 'client',
              defaultValue: '/api/clients/' + this.clientId(),
              type: FieldType.TEXT,
              readonly: false,
              visible: false,
            },
            {
              label: 'rates.originPort',
              placeholder: 'commons.select.placeholder',
              key: 'originPort',
              type: FieldType.SELECT,
              searchable: true,
              options: this.dataStoreService.selectAll<PortModel>('ports')().map((port: PortModel) => ({
                value: port.id,
                label: portNameTemplate(port),
              })),
              defaultValue: '',
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'rates.destinationPort',
              placeholder: 'commons.select.placeholder',
              key: 'destinationPort',
              type: FieldType.SELECT,
              searchable: true,
              options: this.dataStoreService.selectAll<PortModel>('ports')().map((port: PortModel) => ({
                value: port.id,
                label: portNameTemplate(port),
              })),
              defaultValue: '',
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'rates.cargoType',
              placeholder: 'commons.select.placeholder',
              key: 'cargoType',
              type: FieldType.SELECT,
              options: this.dataStoreService.selectAll<CargoTypeModel>('cargoTypes')().map((ct: CargoTypeModel) => ({
                value: ct.id,
                label: ct.name,
              })),
              defaultValue: '',
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'rates.measurementUnit',
              placeholder: 'commons.select.placeholder',
              key: 'measurementUnit',
              type: FieldType.SELECT,
              options: Object.values(RateMeasurementUnit).map((unit) => ({
                value: unit,
                label: this.translateService.instant(`measurementUnits.${unit}`),
              })),
              defaultValue: '',
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'rates.terms',
              placeholder: 'rates.termsPlaceholder',
              key: 'terms',
              type: FieldType.SELECT,
              options: Object.values(RateTerms).map((term) => ({
                value: term,
                label: term,
              })),
              defaultValue: '',
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'rates.currency',
              placeholder: 'commons.select.placeholder',
              key: 'currency',
              type: FieldType.SELECT,
              options: this.dataStoreService.selectAll<CurrencyModel>('currencies')().map((c: CurrencyModel) => ({
                value: c.id,
                label: `${c.code} - ${c.name}`,
              })),
              defaultValue: '',
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'rates.amount',
              placeholder: 'rates.amountPlaceholder',
              key: 'amount',
              type: FieldType.TEXT,
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            }
          ],
        },
      ] as any as ModalTabConfig<ClientRateModel>[]
  );

  actions: TableActionConfig[] = [
    {
      action: TableAction.EDIT,
      isExtra: false,
      shouldDisable: (row: ClientRateModel) => !this.isActiveRate(row),
    },
    {
      action: TableAction.DELETE,
      isExtra: true,
      shouldDisable: (row: ClientRateModel) => !this.isActiveRate(row),
    },
    { action: TableAction.CREATE, isExtra: false },
  ];

  ngOnInit() {
    this.clientId.set(this.route.snapshot.paramMap.get('id'));
    this.globalApiConfigService.configure({
      apiUrl: environment.API_URL,
      updateWithPatch: true,
    });

    this.modelApiService.setContext('rates');
    this.headerConfigurationService.setEndpointHeaders(this.endpoint(), {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        label: 'rates.originPort',
        key: 'originPort',
        format: (id: string) => {
          if (!id) return '-';
          const portObj = this.dataStoreService.getById<PortModel>('ports', id);
          return portObj ? portNameTemplate(portObj) : '-';
        }
      },
      {
        label: 'rates.destinationPort',
        key: 'destinationPort',
        format: (id: string) => {
          if (!id) return '-';
          const portObj = this.dataStoreService.getById<PortModel>('ports', id);
          return portObj ? portNameTemplate(portObj) : '-';
        }
      },
      {
        label: 'rates.cargoType',
        key: 'cargoType',
        format: (id: string) => {
          if (!id) return '-';
          const ctObj = this.dataStoreService.getById<CargoTypeModel>('cargoTypes', id);
          return ctObj ? ctObj.name || '-' : '-';
        }
      },
      {
        label: 'rates.measurementUnit',
        key: 'measurementUnit',
        format: (val: string) => val ? this.translateService.instant(`measurementUnits.${val}`) : '-'
      },
      {
        label: 'rates.terms',
        key: 'terms',
        format: (val: string) => val ? val : '-'
      },
      {
        label: 'rates.currency',
        key: 'currency',
        format: (id: string) => {
          if (!id) return '-';
          const currencyObj = this.dataStoreService.getById<CurrencyModel>('currencies', id);
          return currencyObj ? currencyObj.code || '-' : '-';
        }
      },
      {
        label: 'rates.amount',
        key: 'amount',
        format: (val: string, rate: ClientRateModel) => {
          if (!val) return '-';
          const currencyObj = this.dataStoreService.getById<CurrencyModel>('currencies', rate.currency ?? '');
          return `${currencyObj?.code ?? ''} ${val}`;
        }
      },
      {
        label: 'rates.history',
        key: 'version',
        template: this.historyTemplate,
      }
    ];
  }

  onMoreDataLoaded(event: Map<string, any[]>): void {
    if (!event) return;

    const portsData = event.get('ports');
    if (Array.isArray(portsData)) {
      this.dataStoreService.hydrate<PortModel>(
        'ports',
        portsData.map((port: PortModel) => {
          port.id = portUriTemplate(port.id);
          return port;
        })
      );
    }

    const cargoTypesData = event.get('cargoTypes');
    if (Array.isArray(cargoTypesData)) {
      this.dataStoreService.hydrate<CargoTypeModel>(
        'cargoTypes',
        cargoTypesData.map((ct: CargoTypeModel) => {
          ct.id = cargoTypeUriTemplate(ct.id);
          return ct;
        })
      );
    }

    const currenciesData = event.get('currencies');
    if (Array.isArray(currenciesData)) {
      this.dataStoreService.hydrate<CurrencyModel>(
        'currencies',
        currenciesData.map((c: CurrencyModel) => {
          c.id = currencyUriTemplate(c.id);
          return c;
        })
      );
    }
  }

  handleApiError = (error: any, defaultMessage: string, operation: string): string => {
    const errObj = error?.error || error;
    if (errObj?.status === 422 && Array.isArray(errObj?.violations)) {
      const violation = errObj.violations.find((v: any) =>
        v.code === '23bd9dbf-6b9b-41cd-a99e-4844bcf3077f' ||
        v.message?.includes('exact combination')
      );
      if (violation) {
        return this.translateService.instant('rates.errors.duplicateRate');
      }
    }
    if (errObj?.status === 409) {
      return this.translateService.instant('rates.errors.historicalRateConflict');
    }
    return defaultMessage;
  };

  get contactFields(): ModalFieldConfig<any>[] {
    return [];
  }

  onFieldValueChange(key: string, value: any): void {
  }

  ratesModelFactory = (data: any): ClientRateModel => {
    return new ClientRateModel().fromJSON(data);
  };

  isActiveRate(rate: ClientRateModel | null | undefined): boolean {
    return rate?.isActive !== false;
  }

  formatDisabledAt(disabledAt: string | null | undefined): string {
    if (!disabledAt) {
      return '-';
    }

    return new Date(disabledAt).toLocaleString();
  }

  refreshRatesTable(): void {
    setTimeout(() => {
      this.ratesTable?.onManualRefresh();
    });
  }

  ngOnDestroy(): void {
    this.globalApiConfigService.configure({
      apiUrl: environment.API_URL,
      updateWithPatch: false,
    });
  }

  globalCustomActions = computed<GlobalAction<ClientRateModel>[]>(() => [
    {
      action: 'goBackToClients',
      label: this.translateService.instant('clients.back_to_clients'),
      icon: 'icon-arrow-left',
      callback: () => this.goBackToClients(),
      class: 'c-btn c-btn--stroked',
    }
  ]);

  createButtonConfig = signal<Partial<ButtonConfig>>({
    text: this.translateService.instant('rates.addRate'),
    type: ButtonType.PRIMARY
  });

  goBackToClients(): void {
    this.router.navigate(['/app/clients']);
  }
}
